import React from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import { MaterialIcons } from '@expo/vector-icons';
import { ChargerAttributes } from '../models/charger-model';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import { getPriceInfo } from './common/utils/get-price-info';
import * as Location from 'expo-location';
import ChargerImageLoader from './ChargerImageLoader';
import ChargerStatusBadge from './ChargerStatusBadge';

interface SearchChargerCardProps {
  data: ChargerAttributes;
  isSelected: boolean;
  loading: boolean;
  driverPlan: UserPlanAssociation | null;
  onSelectCharger: (charger: ChargerAttributes) => void;
}

const SearchChargerCard: React.FC<SearchChargerCardProps> = ({
  data,
  isSelected,
  loading,
  driverPlan,
  onSelectCharger,
}) => {
  const { currentPrice, priceLabel, nextTransition, todayPeak } = getPriceInfo(
    data,
    driverPlan
  );

  const shouldRenderExtraInfo = Boolean(
    (todayPeak &&
      typeof todayPeak === 'object' &&
      todayPeak.start !== todayPeak.end &&
      priceLabel &&
      typeof priceLabel === 'string' &&
      priceLabel.trim().length > 0) ||
      (nextTransition &&
        typeof nextTransition === 'string' &&
        nextTransition.trim().length > 0)
  );

  return (
    <View
      style={[styles.cardContainer, loading && !isSelected && styles.dimmed]}
    >
      <Pressable
        disabled={loading || isSelected}
        onPress={() => onSelectCharger(data)}
        style={({ pressed }) => [pressed && { opacity: 0.6 }, styles.pressable]}
        accessibilityRole="button"
      >
        <View style={styles.cardRow}>
          <View style={styles.cardLeftSection}>
          <ChargerImageLoader
            chargerId={data?.uuid}
            style={styles.chargerImage}
            fallbackSource={require('../../assets/chargerStation.png')}
            resizeMode="cover"
            alt="Charger station"
            circular={true}
          />
            <View>
              <Text style={[styles.cardTitle, styles.alignEllipses]}>
                {String(data?.name || 'Unknown')}
              </Text>
              <Text style={[styles.textColor, styles.alignEllipses]}>
                {String(data?.full_address || '')}
              </Text>
              <Text style={[styles.textColor, styles.alignEllipses]}>
                {String(data?.description || '')}
              </Text>
            </View>
          </View>
          <View style={styles.cardRightSection}>
            <FontAwesome6 name="location-dot" size={16} color="#8D8E90" />
            <Text style={styles.textColor}>
              {String(data?.totalDistance || 0)} miles
            </Text>
            <MaterialIcons
              name="keyboard-arrow-right"
              size={24}
              color="white"
            />
          </View>
        </View>

        {/* Loading Spinner */}
        {isSelected && loading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color="#ffffff" />
          </View>
        )}

        {/* Charger Type and Status */}
        <View style={styles.chargerTypeRow}>
          <View style={styles.chargerTypeSection}>
            <View style={styles.chargerTypeLeft}>
              <Image
                source={require('../../assets/chargerType.png')}
                style={styles.chargerTypeImage}
              />
              <View>
                <Text style={styles.chargerTypeText}>
                  {String(data?.charger_model || '') +
                    ' - ' +
                    String(data?.charger_type || '') +
                    ' Charger'}
                </Text>
                <Text style={styles.priceText}>
                  {' ' +
                    (typeof currentPrice === 'number'
                      ? currentPrice.toFixed(3)
                      : '0.000') +
                    '/kWh'}
                </Text>
              </View>
            </View>
            {shouldRenderExtraInfo && (
              <View style={styles.extraInfoSection}>
                {todayPeak &&
                  typeof todayPeak === 'object' &&
                  todayPeak.start !== todayPeak.end &&
                  priceLabel &&
                  typeof priceLabel === 'string' &&
                  priceLabel.trim().length > 0 && (
                    <Text style={styles.extraInfo}>
                      {' (' + String(priceLabel) + ')'}
                    </Text>
                  )}
                {nextTransition &&
                  typeof nextTransition === 'string' &&
                  nextTransition.trim().length > 0 && (
                    <Text style={styles.extraInfo}>
                      {' [' + String(nextTransition) + ']'}
                    </Text>
                  )}
              </View>
            )}
          </View>
          <ChargerStatusBadge 
            charger={data}
            size="medium"
          />
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  dimmed: {
    opacity: 0.5,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
  pressable: {
    position: 'relative',
  },
  cardContainer: {
    backgroundColor: '#292D33',
    marginVertical: 5,
    borderRadius: 10,
    padding: 5,
  },
  chargerImage: {
    width: 50,
    height: 50,
  },
  chargerTypeImage: {
    width: 33,
    height: 33,
  },
  alignEllipses: Platform.select({
    web: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
    default: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
  }),
  textColor: {
    color: '#8D8E90',
  },
  extraInfo: {
    fontSize: 12,
    color: '#39BEB7',
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
  },
  cardLeftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  cardRightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
  },
  chargerTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
  },
  chargerTypeSection: {
    alignItems: 'center',
  },
  chargerTypeLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  chargerTypeText: {
    fontSize: 12,
    color: '#E8E8E9',
  },
  priceText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
  extraInfoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
  },
});

export default SearchChargerCard;
